import { forwardRef, useEffect, useRef, type InputHTMLAttributes, type ReactNode } from 'react'

// P011: thin wrapper around <input type="checkbox"> — control-first label
// (matches the existing raw report-page checkbox sites, unlike Toggle's
// form-row switch-right layout). `indeterminate` has no React prop — it's a
// DOM-only property — so we merge the forwarded ref with an internal one and
// set it imperatively in a useEffect, mirroring InvoiceGeneratePage's header
// checkbox idiom.
export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: ReactNode
  variant?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'error' | 'info'
  size?: 'sm' | 'md'
  indeterminate?: boolean
}

const VARIANT_CLASSES: Record<NonNullable<CheckboxProps['variant']>, string> = {
  primary: 'checkbox-primary',
  secondary: 'checkbox-secondary',
  accent: 'checkbox-accent',
  success: 'checkbox-success',
  warning: 'checkbox-warning',
  error: 'checkbox-error',
  info: 'checkbox-info',
}

const SIZE_CLASSES: Record<NonNullable<CheckboxProps['size']>, string> = {
  sm: 'checkbox-sm',
  md: '',
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className = '', label, variant, size = 'md', indeterminate = false, disabled, ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate
      }
    }, [indeterminate])

    const setRefs = (el: HTMLInputElement | null) => {
      internalRef.current = el
      if (typeof ref === 'function') {
        ref(el)
      } else if (ref) {
        ref.current = el
      }
    }

    const input = (
      <input
        ref={setRefs}
        type="checkbox"
        disabled={disabled}
        className={`checkbox ${variant ? VARIANT_CLASSES[variant] : ''} ${SIZE_CLASSES[size]} ${className}`}
        {...props}
      />
    )

    if (!label) return input

    return (
      <label className={`flex items-center gap-2 text-sm cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
        {input}
        <span>{label}</span>
      </label>
    )
  }
)

Checkbox.displayName = 'Checkbox'
